import { useEffect, useCallback, useRef } from 'react';
import { DISTANCE_LABELS_LAYER } from '../constants';

/**
 * Hook for automatically hiding distance labels at city zoom levels in VRP
 * Based on TSP implementation but adapted for VRP's multi-vehicle context
 * @param {Object} map - Mapbox map instance
 * @param {boolean} mapReady - Whether map is ready
 * @param {boolean} userControlledVisibility - User's preference from layer control
 * @param {Function} onAutoVisibilityChange - Callback when auto visibility changes
 * @param {Function} onUserPreferenceChange - Callback when user preference should be updated
 * @returns {Object} Auto-hide state and controls
 */
export const useAutoHideDistanceLabels = (
  map, 
  mapReady,
  userControlledVisibility,
  onAutoVisibilityChange,
  onUserPreferenceChange
) => {
  // Zoom level threshold for city level
  const CITY_ZOOM_THRESHOLD = 8;
  
  // Current auto-hide state
  const autoHiddenRef = useRef(false);
  const lastZoomRef = useRef(null);
  const userPreferenceRef = useRef(userControlledVisibility);
  
  // Update user preference reference
  useEffect(() => {
    userPreferenceRef.current = userControlledVisibility;
  }, [userControlledVisibility]);

  // Check if distance labels should be auto-hidden
  const shouldAutoHide = useCallback((zoom) => {
    return zoom <= CITY_ZOOM_THRESHOLD;
  }, []);

  // Update distance labels visibility
  const updateDistanceLabelsVisibility = useCallback((zoom, force = false) => {
    if (!map || !mapReady) return;

    const shouldHide = shouldAutoHide(zoom);
    
    // Skip if zoom level hasn't crossed threshold and not forced update
    if (!force && 
        lastZoomRef.current !== null && 
        shouldAutoHide(lastZoomRef.current) === shouldHide) {
      return;
    }

    try {
      if (map.getLayer(DISTANCE_LABELS_LAYER)) {
        let finalVisibility;
        
        if (shouldHide) {
          // At city level, unconditionally force hide distance labels
          finalVisibility = 'none';
          autoHiddenRef.current = true; // Auto-hidden
        } else {
          // At non-city level
          // If previously auto-hidden, now auto-enable display
          if (autoHiddenRef.current) {
            userPreferenceRef.current = true; // Auto-set user preference to enabled
            // Notify layer control to update state
            if (onUserPreferenceChange) {
              onUserPreferenceChange(true);
            }
          }
          
          finalVisibility = userPreferenceRef.current ? 'visible' : 'none';
          autoHiddenRef.current = false; // Non-city level is not considered auto-hidden
        }

        map.setLayoutProperty(DISTANCE_LABELS_LAYER, 'visibility', finalVisibility);

        // Notify external components of auto-visibility state changes
        if (onAutoVisibilityChange) {
          onAutoVisibilityChange({
            zoom,
            shouldAutoHide: shouldHide,
            isAutoHidden: autoHiddenRef.current,
            finalVisibility: finalVisibility === 'visible'
          });
        }
      }
    } catch (error) {
      console.error('[VRP] Error updating distance labels auto-hide visibility:', error);
    }

    lastZoomRef.current = zoom;
  }, [map, mapReady, shouldAutoHide, onAutoVisibilityChange, onUserPreferenceChange]);

  // Listen to map zoom events
  useEffect(() => {
    if (!map || !mapReady) return;

    const handleZoomEnd = () => {
      const currentZoom = map.getZoom();
      updateDistanceLabelsVisibility(currentZoom);
    };

    // Immediately check current zoom level
    if (map.loaded()) {
      const currentZoom = map.getZoom();
      updateDistanceLabelsVisibility(currentZoom, true); // Force initial update
    }

    map.on('zoomend', handleZoomEnd);
    
    return () => {
      map.off('zoomend', handleZoomEnd);
    };
  }, [map, mapReady, updateDistanceLabelsVisibility]);

  // Force update visibility when user preference changes
  useEffect(() => {
    if (map && mapReady && lastZoomRef.current !== null) {
      updateDistanceLabelsVisibility(lastZoomRef.current, true);
    }
  }, [map, mapReady, userControlledVisibility, updateDistanceLabelsVisibility]);

  // Return state and control functions
  return {
    isAutoHidden: autoHiddenRef.current,
    cityZoomThreshold: CITY_ZOOM_THRESHOLD,
    shouldAutoHide: shouldAutoHide,
    forceUpdateVisibility: () => {
      if (map && mapReady) {
        const currentZoom = map.getZoom();
        updateDistanceLabelsVisibility(currentZoom, true);
      }
    }
  };
};

